import axios, { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import { API_URL } from "../consts";
import { Messaggio } from "../prismaTypes";
import { Loading } from "../ui/Loading";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { format } from "date-fns";
import { memoParseISO } from "../utils/dateUtils";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCallback, useState } from "react";
import { queryClient } from "../App";
import { useSnackbar } from "notistack";

export function MessaggiAdminTable(props: { onEdit: (mess: Messaggio) => void }) {
  const { onEdit } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(-1);

  const openDelete = (id: number) => {
    setDeleteId(id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const { isLoading, data } = useQuery<unknown, AxiosError, { messaggi: Messaggio[] }>(
    ["messaggi"],
    () => axios.get(API_URL + "messaggi").then((res) => res.data)
  );

  const { mutate } = useMutation(
    (id: number) => {
      return axios.delete(API_URL + "messaggi/" + id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Comunicazione eliminata", { variant: "success" });
      },
      onError: (error: AxiosError) => {
        queryClient.invalidateQueries();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const eliminaClick = useCallback(() => {
    mutate(deleteId);
    setOpen(false);
  }, [mutate, setOpen, deleteId]);

  if (isLoading) return <Loading />;

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Titolo</TableCell>
              <TableCell>Attivo</TableCell>
              <TableCell>Dal</TableCell>
              <TableCell>Al</TableCell>
              <TableCell>Permanente</TableCell>
              <TableCell>Al Login</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.messaggi?.map((mess) => (
              <TableRow key={mess.id}>
                <TableCell>
                  <IconButton sx={{ p: 0 }} onClick={() => onEdit(mess)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
                <TableCell>{mess.titolo}</TableCell>
                <TableCell>{mess.attivo ? "Si" : "No"}</TableCell>
                <TableCell>
                  {mess.dataDal && format(memoParseISO(mess.dataDal.slice(0, -1)), "dd/MM/yyyy")}
                </TableCell>
                <TableCell>
                  {mess.dataFine && format(memoParseISO(mess.dataFine.slice(0, -1)), "dd/MM/yyyy")}
                </TableCell>
                <TableCell>{mess.displayOnSide ? "Si" : "No"}</TableCell>
                <TableCell>{mess.displayOnLogin ? "Si" : "No"}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <IconButton
                    aria-label="elimina"
                    sx={{ p: 0 }}
                    onClick={() => openDelete(mess.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={open}>
        <DialogContent>Sei sicuro di voler eliminare?</DialogContent>
        <DialogActions>
          <Button onClick={eliminaClick} autoFocus variant="text" color="error">
            Elimina
          </Button>
          <Button onClick={handleClose}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
